import { useEffect, useCallback, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { TakeoffSVG } from '../svgElements/TakeoffSVG';
import { useUnityBuildStore } from '../../stores/store';
import { ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import '../../App.css';
import { useFetchObject } from '../../hooks/useFetchObject';
import { HIGH_RES_SCALE } from '../../utils/constants';
import { useStore } from 'zustand';
import { useTheme, alpha } from '@mui/material';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useQueryClient } from 'react-query';

// Import responsive utilities
import { useResponsive, useOrientation, ResponsiveHelpers } from '../../theme';
import React from 'react';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

const DocumentViewport = () => {
  const theme = useTheme();
  const responsive = useResponsive() as ResponsiveHelpers;
  const { isMobile } = responsive;
  const { isLandscape } = useOrientation();
  const queryClient = useQueryClient();

  const isDragging = useStore(useUnityBuildStore, state => state.isDragging);
  const selectedObject = useStore(useUnityBuildStore, state => state.selectedObject);
  const currentPage = useStore(useUnityBuildStore, state => state.currentPage);
  const setNumPages = useStore(useUnityBuildStore, state => state.setNumPages);
  const setPdfDimensions = useStore(useUnityBuildStore, state => state.setPdfDimensions);
  const setCurrentPage = useStore(useUnityBuildStore, state => state.setCurrentPage);

  const { data: blobURL, isLoading, isError } = useFetchObject(selectedObject?.gcsobjectname ?? '');

  const transformWrapperRef = useRef<ReactZoomPanPinchRef | null>(null);
  const pdfDocumentRef = useRef<any>(null);

  // Track previously loaded documents to unload them when not needed
  const prevObjectNameRef = useRef<string | null>(null);

  const [containerDimensions, setContainerDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const [originalDimensions, setOriginalDimensions] = useState<{
    width: number;
    height: number;
  } | null>(null);

  useEffect(() => {
    const handleResize = () => {
      setContainerDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Memory cleanup when changing documents
  useEffect(() => {
    // If the selected object has changed, clean up the previous document
    if (
      prevObjectNameRef.current &&
      prevObjectNameRef.current !== selectedObject?.gcsobjectname &&
      prevObjectNameRef.current !== ''
    ) {
      // Force garbage collection of previous PDF document
      if (pdfDocumentRef.current) {
        pdfDocumentRef.current.cleanup();
        pdfDocumentRef.current = null;
      }

      // Reset dimensions
      setOriginalDimensions(null);
    }

    // Update the reference to the current document name
    prevObjectNameRef.current = selectedObject?.gcsobjectname ?? null;

    // Clean up when component unmounts
    return () => {
      if (pdfDocumentRef.current) {
        pdfDocumentRef.current.cleanup();
      }
    };
  }, [selectedObject]);

  const fileExtension = selectedObject?.gcsobjectname?.split('.').pop()?.toLowerCase();

  /**
   * Unified function to apply transformations for both PDFs and Images
   */
  const setTransformForContent = useCallback(
    (contentWidth: number, contentHeight: number) => {
      if (transformWrapperRef.current) {
        const { width: containerWidth, height: containerHeight } = containerDimensions;
        const scaleX = containerWidth / contentWidth;
        const scaleY = containerHeight / contentHeight;
        const scale = Math.min(scaleX, scaleY) * 0.9; // Scale down slightly for better fit
        const x = (containerWidth - contentWidth * scale) / 2;
        const y = (containerHeight - contentHeight * scale) / 2;
        transformWrapperRef.current.setTransform(x, y, scale);
      }
    },
    [containerDimensions]
  );

  /**
   * Handle PDF loading and apply the same transformation logic
   */
  const onDocumentLoadSuccess = useCallback(
    (pdf: any) => {
      pdfDocumentRef.current = pdf;
      setNumPages(pdf.numPages);
      setCurrentPage(1);
      pdf.getPage(1).then((page: any) => {
        const viewport = page.getViewport({ scale: 1 });
        const dimensions = { width: viewport.width, height: viewport.height };
        setPdfDimensions(dimensions);
        setOriginalDimensions(dimensions);
        setTransformForContent(dimensions.width, dimensions.height); // Apply same transform logic
      });
    },
    [setNumPages, setCurrentPage, setPdfDimensions, setTransformForContent]
  );

  /**
   * Handle Image Loading with the same transform logic
   */
  useEffect(() => {
    if (['png', 'jpg', 'jpeg', 'webp'].includes(fileExtension || '')) {
      if (selectedObject?.width && selectedObject?.height) {
        const dimensions = {
          width: selectedObject.width,
          height: selectedObject.height,
        };
        setPdfDimensions(dimensions);
        setOriginalDimensions(dimensions);
        setTransformForContent(dimensions.width, dimensions.height); // Apply same transform logic
      }
    }
  }, [selectedObject, fileExtension, setPdfDimensions, setTransformForContent]);

  // Check memory usage periodically
  useEffect(() => {
    const checkMemoryInterval = setInterval(() => {
      // Report memory usage if available in Chrome
      if (window.performance) {
        const perf = window.performance as any;
        if (perf.memory) {
          const memUsed = Math.round(perf.memory.usedJSHeapSize / (1024 * 1024));

          // If memory usage is high, force clear non-active document caches
          if (memUsed > 200) {
            // 200MB threshold
            console.warn(`High memory usage detected: ${memUsed}MB - performing cleanup`);

            // Get all document queries and clear those not matching current document
            const allQueries = queryClient.getQueryCache().findAll(['object']);

            allQueries.forEach(query => {
              const queryKey = query.queryKey as any[];
              // Keep only the currently selected document in cache
              if (queryKey[1] !== selectedObject?.gcsobjectname) {
                queryClient.removeQueries(query.queryKey);
              }
            });

            // Force garbage collection hint
            if (global.gc) {
              try {
                global.gc();
              } catch (e) {
                // GC not available
              }
            }
          }
        }
      }
    }, 30000); // Check every 30 seconds

    return () => clearInterval(checkMemoryInterval);
  }, [queryClient, selectedObject]);

  // Background color for the document viewport based on theme
  const bgColor =
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.grey[900], 0.9) // Darker background for dark mode
      : alpha(theme.palette.grey[200], 0.9); // Lighter background for light mode

  // Adjust display characteristics based on device type and orientation
  const getViewportStyles = (): React.CSSProperties => {
    const baseStyles = {
      backgroundColor: bgColor,
      width: '100%',
      height: '100%',
      overflow: 'hidden' as const,
      position: 'relative' as const,
    };

    if (isMobile) {
      if (!isLandscape) {
        // Show a warning or notification for mobile portrait mode
        return {
          ...baseStyles,
          display: 'flex' as const,
          flexDirection: 'column' as const,
          justifyContent: 'center' as const,
          alignItems: 'center' as const,
          padding: 2,
        };
      }
      // Mobile landscape optimizations
      return {
        ...baseStyles,
      };
    }

    return baseStyles;
  };

  let content: JSX.Element;

  if (isLoading) {
    content = (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: theme.palette.text.primary,
        }}
      >
        <CircularProgress size={isMobile ? 32 : 40} color="primary" />
        <Typography variant={isMobile ? 'body2' : 'body1'} sx={{ mt: 2 }}>
          Loading document...
        </Typography>
      </Box>
    );
  } else if (isError) {
    content = (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: theme.palette.error.main,
          p: 2,
        }}
      >
        <Typography variant={isMobile ? 'subtitle1' : 'h6'}>Error loading document</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
          Please try selecting another document or refresh the page.
        </Typography>
      </Box>
    );
  } else if (!selectedObject) {
    content = (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: theme.palette.text.primary,
          p: 2,
        }}
      >
        <Typography variant={isMobile ? 'subtitle1' : 'h6'}>No document selected</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
          Please select a document from the dropdown above.
        </Typography>
      </Box>
    );
  } else if (isMobile && !isLandscape) {
    // Special content for mobile portrait mode
    content = (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          p: 3,
          color: theme.palette.warning.main,
          textAlign: 'center',
        }}
      >
        <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
          Please rotate your device
        </Typography>
        <Typography variant="body2" color="text.secondary">
          This application is optimized for landscape orientation on mobile devices for construction
          documents.
        </Typography>
      </Box>
    );
  } else if (fileExtension === 'pdf') {
    if (!originalDimensions) {
      content = (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
          }}
        >
          <Document file={blobURL} onLoadSuccess={onDocumentLoadSuccess}>
            <Page pageNumber={currentPage} renderAnnotationLayer={false} scale={1} />
          </Document>
        </Box>
      );
    } else {
      content = (
        <div
          style={{
            width: `${originalDimensions.width}px`,
            height: `${originalDimensions.height}px`,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              transform: `scale(${1 / HIGH_RES_SCALE})`,
              transformOrigin: 'top left',
            }}
          >
            <Document file={blobURL} onLoadSuccess={onDocumentLoadSuccess}>
              <Page
                pageNumber={currentPage}
                renderAnnotationLayer={false}
                scale={HIGH_RES_SCALE}
                loading={
                  <CircularProgress
                    size={isMobile ? 32 : 40}
                    color="primary"
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      marginTop: -2,
                      marginLeft: -2,
                    }}
                  />
                }
              />
            </Document>
          </div>
        </div>
      );
    }
  } else if (['png', 'jpg', 'jpeg', 'webp'].includes(fileExtension || '')) {
    content = (
      <div
        style={{
          width: selectedObject?.width ?? 'auto',
          height: selectedObject?.height ?? 'auto',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <img
          className="document-image"
          src={blobURL as string}
          alt="document"
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
            margin: '0 auto',
            pointerEvents: 'auto',
          }}
          onLoad={() => {
            // Clear cached versions of previous documents when a new one loads successfully
            if (
              prevObjectNameRef.current &&
              prevObjectNameRef.current !== selectedObject?.gcsobjectname
            ) {
              // Find and remove previous document queries from cache
              const allQueries = queryClient.getQueryCache().findAll(['object']);
              allQueries.forEach(query => {
                const queryKey = query.queryKey as any[];
                if (queryKey[1] !== selectedObject?.gcsobjectname) {
                  queryClient.removeQueries(query.queryKey);
                }
              });
            }
          }}
        />
      </div>
    );
  } else {
    content = (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          p: 2,
          color: theme.palette.warning.main,
        }}
      >
        <Typography variant={isMobile ? 'subtitle1' : 'h6'}>Unsupported file type</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
          This file format cannot be previewed. Supported formats are PDF, PNG, JPG, and JPEG and
          WEBP.
        </Typography>
      </Box>
    );
  }

  return (
    <TransformWrapper
      ref={transformWrapperRef}
      initialScale={1}
      minScale={0.25}
      maxScale={5}
      limitToBounds={false}
      doubleClick={{ disabled: true }}
      panning={{ velocityDisabled: true, disabled: isDragging }}
      wheel={{ smoothStep: 0.002 }}
    >
      {() => (
        <TransformComponent
          wrapperStyle={getViewportStyles()}
          contentStyle={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            outline: 'none', // Remove outline on focus
          }}
        >
          {content}
          <TakeoffSVG />
        </TransformComponent>
      )}
    </TransformWrapper>
  );
};

export default React.memo(DocumentViewport);
